import { useAsync } from 'react-use';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { Entity } from '@backstage/catalog-model';

export interface BusinessUnitInfo {
  resolvedBusinessUnit: string;
  fromLeanIX?: string;
  isValidAppId?: boolean;
}

export function useBusinessUnitInfo(entity: Entity): {
  businessUnitInfo: BusinessUnitInfo | undefined;
  loading: boolean;
  error?: Error;
} {
  const catalogApi = useApi(catalogApiRef);

  const {
    value: businessUnitInfo,
    loading,
    error,
  } = useAsync(async () => {
    if (
      entity.kind.toLowerCase() === 'system' &&
      entity.spec?.type === 'leanix appid'
    ) {
      const orgUnit = (entity.metadata?.orgUnit as Record<string, any>)?.level2;
      if (!orgUnit || orgUnit?.length === 0) {
        return undefined;
      }
      return {
        resolvedBusinessUnit: orgUnit,
      } as BusinessUnitInfo;
    }
    if (entity.kind.toLowerCase() !== 'component') {
      return undefined;
    }

    try {
      const backendOrgUnit = entity.spec?.['pfg-org-unit'] as string;

      // If backend provided a valid business unit (not unknown), use it
      if (backendOrgUnit && !backendOrgUnit.includes('unknown-business-unit')) {
        const buData = backendOrgUnit.split('.');
        const resolvedBusinessUnit =
          buData?.length >= 2 ? buData[1] : 'unknown-business-unit';

        // Format the business unit name
        const businessUnit = resolvedBusinessUnit.split('-');
        let resolvedBusinessUnitFormatted = '';

        if (resolvedBusinessUnit === 'principal-international') {
          // Special case for Principal International business unit
          resolvedBusinessUnitFormatted = 'Principal International';
        } else {
          // Handle all other business units including principal-asset-management
          for (let index = 0; index < businessUnit.length; index++) {
            const word = businessUnit[index];
            resolvedBusinessUnitFormatted += `${word
              .charAt(0)
              .toUpperCase()}${word.slice(1)} `;
          }
          resolvedBusinessUnitFormatted = resolvedBusinessUnitFormatted.trim();
        }

        // Determine if this came from LeanIX by checking if AppID exists in system entities
        let isFromLeanIX = false;
        const appId =
          entity.metadata?.labels?.['pfg-app-inventory-id']?.toLowerCase();

        if (appId && appId !== 'no-entry') {
          try {
            // Fetch all system entities to check if AppID is valid
            const allSystemEntities = await catalogApi.getEntities({
              filter: {
                kind: 'System',
              },
            });

            // Create array of valid AppIDs from system entity names
            const validAppIds = allSystemEntities.items
              .map(system => {
                const systemName = system.metadata?.name;
                if (systemName && systemName.startsWith('APPID-')) {
                  return systemName.toLowerCase();
                }
                return undefined;
              })
              .filter((id): id is string => !!id);

            // If AppID exists in system entities, then LeanIX rule was used
            isFromLeanIX = validAppIds.includes(appId);
          } catch (validationError) {
            // If validation fails, assume not from LeanIX
            isFromLeanIX = false;
          }
        }

        return {
          resolvedBusinessUnit: resolvedBusinessUnitFormatted,
          fromLeanIX: isFromLeanIX ? resolvedBusinessUnitFormatted : undefined,
          isValidAppId: isFromLeanIX,
        } as BusinessUnitInfo;
      }

      let fromLeanIX: string | undefined;
      let isValidAppId = false;

      // Check if this component has a LeanIX app ID
      const appId = entity.metadata?.labels?.['pfg-app-inventory-id'];

      if (appId && appId !== 'no-entry') {
        try {
          // Find the corresponding System entity
          const allSystemEntities = await catalogApi.getEntities({
            filter: {
              kind: 'System',
            },
          });

          // Gather all valid AppIDs from system entities (labels and names)
          const validAppIds = allSystemEntities.items
            .flatMap(system => {
              const ids: string[] = [];
              // Include AppID from labels if present
              const labelAppId =
                system.metadata?.labels?.['pfg-app-inventory-id'];
              if (labelAppId) {
                ids.push(labelAppId);
              }
              // Include AppID extracted from name if it matches APPID-* pattern
              const name = system.metadata?.name || '';
              if (name.toUpperCase().startsWith('APPID-')) {
                const nameAppId = name.toLowerCase();
                ids.push(nameAppId);
              }
              return ids;
            })
            .filter((id): id is string => !!id);

          // Check if the component's AppID is valid (exists in system entities)
          isValidAppId = validAppIds.includes(appId);

          // Only proceed with LeanIX lookup if AppID is valid
          if (isValidAppId) {
            const matchingSystemEntities = allSystemEntities.items.filter(
              system => {
                const systemAppId =
                  system.metadata?.labels?.['pfg-app-inventory-id'];
                const systemName = system.metadata?.name || '';
                const appIdUppercase = appId
                  .toUpperCase()
                  .replace('appid-', 'APPID-');
                const nameMatches = systemName.toUpperCase() === appIdUppercase;

                return systemAppId === appId || nameMatches;
              },
            );

            if (matchingSystemEntities.length > 0) {
              const appIdUppercase = appId
                .toUpperCase()
                .replace('appid-', 'APPID-');

              const appIdSystemEntity = matchingSystemEntities.find(
                system =>
                  system.metadata?.name?.toUpperCase() === appIdUppercase,
              );

              const systemEntity =
                appIdSystemEntity || matchingSystemEntities[0];

              fromLeanIX = (systemEntity.metadata as any)?.orgUnit?.level2;
            }
          }
        } catch (systemLookupError) {
          // Failed to lookup System entity for business unit
        }
      }

      if (fromLeanIX && isValidAppId) {
        const result = {
          resolvedBusinessUnit: fromLeanIX,
          fromLeanIX,
          isValidAppId: true,
        } as BusinessUnitInfo;

        return result;
      }

      const orgUnit =
        backendOrgUnit || (entity.spec?.['pfg-org-unit'] as string);

      const buData = orgUnit?.split('.');
      const resolvedBusinessUnit =
        buData?.length >= 2 ? buData[1] : 'unknown-business-unit';

      // Format business unit name
      const businessUnit = resolvedBusinessUnit.split('-');
      let resolvedBusinessUnitFormatted = '';

      if (resolvedBusinessUnit === 'unknown-business-unit') {
        resolvedBusinessUnitFormatted = 'Unknown Business Unit';
      } else if (resolvedBusinessUnit === 'principal-international') {
        // Special case for Principal International business unit
        resolvedBusinessUnitFormatted = 'Principal International';
      } else {
        // Handle all other business units including principal-asset-management
        for (let index = 0; index < businessUnit.length; index++) {
          const word = businessUnit[index];
          resolvedBusinessUnitFormatted += `${word
            .charAt(0)
            .toUpperCase()}${word.slice(1)} `;
        }
        resolvedBusinessUnitFormatted = resolvedBusinessUnitFormatted.trim();
      }

      const result = {
        resolvedBusinessUnit: resolvedBusinessUnitFormatted,
        fromLeanIX,
        isValidAppId,
      } as BusinessUnitInfo;

      return result;
    } catch (err) {
      throw new Error(
        `Failed to fetch Business Unit from LeanIX: ${err} .Please refresh the page.`,
      );
    }
  }, [entity, catalogApi]);

  return {
    businessUnitInfo,
    loading,
    error,
  };
}
